import { defaultSyntheticKlvTemplateJson, parseSyntheticKlvTemplateDocument } from "./syntheticKlvTemplate.mjs";
export const patternKlvTemplateJson = JSON.stringify({ ...JSON.parse(defaultSyntheticKlvTemplateJson), 11: "PATTERN" }, null, 2);
export const patternNames = ["smpte", "ball", "checkerboard", "snow", "black", "white", "red", "green", "blue"];
export const patternDefaults = Object.freeze({ width: 1280, height: 720, fps_num: 30, fps_den: 1,
  codec: 1, bitrate: 2000, max_bitrate: 2000, keyframe: 60, acceleration: 0, rate_control: 0, time: 0, counter: 0,
  klv_enabled: 0, klv_pid: 258, klv_type: 1, klv_every_frame: 1, klv_diagnostics: 0, klv_rate: 10, klv_revision: 16, klv_template: patternKlvTemplateJson });
export function parsePattern(value = "") {
  const input = String(value).replace(/^pattern:\/\//, "");
  const [name, query = ""] = input.split("?");
  const params = new URLSearchParams(query);
  const config = { pattern: name || "smpte", ...patternDefaults };
  for (const key of Object.keys(patternDefaults)) if (params.has(key)) config[key] = params.get(key);
  if (!params.has("max_bitrate")) config.max_bitrate = config.bitrate;
  return config;
}
export function patternValue(config) {
  const params = new URLSearchParams();
  try { config = { ...config, klv_revision: parseSyntheticKlvTemplateDocument(config.klv_template ?? patternKlvTemplateJson).revision }; } catch {}
  for (const key of Object.keys(patternDefaults)) params.set(key, String(config[key] ?? patternDefaults[key]));
  return `${config.pattern || "smpte"}?${params}`;
}
// Keep KLV settings in the stored URI while leaving the editable URL readable.
export function visiblePatternUrl(value) {
  const uri = String(value ?? "");
  if (!uri.startsWith("pattern://") || !uri.includes("?")) return uri;
  const [path, query] = uri.split("?", 2);
  const params = new URLSearchParams(query);
  for (const key of [...params.keys()]) if (key.startsWith("klv_")) params.delete(key);
  const visibleQuery = params.toString();
  return visibleQuery ? `${path}?${visibleQuery}` : path;
}
export function withHiddenPatternKlv(currentValue, nextValue) {
  const currentUri = String(currentValue ?? "");
  const nextUri = String(nextValue ?? "");
  if (!currentUri.startsWith("pattern://") || !nextUri.startsWith("pattern://")) return nextUri;
  const currentParams = new URLSearchParams(currentUri.split("?", 2)[1] ?? "");
  const [path, query = ""] = nextUri.split("?", 2);
  const params = new URLSearchParams(query);
  for (const [key, value] of currentParams) {
    if (key.startsWith("klv_") && !params.has(key)) params.set(key, value);
  }
  const mergedQuery = params.toString();
  return mergedQuery ? `${path}?${mergedQuery}` : path;
}
export function validatePattern(value) {
  const config = parsePattern(value);
  if (!patternNames.includes(config.pattern)) return "Unknown Pattern source";
  const bounds = { width: [1,16384], height: [1,16384], fps_num: [1,2147483647], fps_den: [1,2147483647],
    bitrate: [1,100000], max_bitrate: [1,100000], keyframe: [1,1000000], codec: [1,2], acceleration: [0,2], rate_control: [0,1], time: [0,1], counter: [0,1],
    klv_enabled: [0,1], klv_type: [0,1], klv_every_frame: [0,1], klv_diagnostics: [0,1], klv_pid: [32,8186], klv_revision: [1,19] };
  for (const [key, [min,max]] of Object.entries(bounds)) {
    if (!/^\d+$/.test(String(config[key])) || Number(config[key]) < min || Number(config[key]) > max)
      return `Pattern ${key.replaceAll("_", " ")} must be an integer from ${min} to ${max}`;
  }
  if (Number(config.fps_num) / Number(config.fps_den) > 240 || Number(config.fps_num) < Number(config.fps_den)) return "Pattern FPS must be from 1 to 240";
  if (Number(config.rate_control) === 0 && Number(config.max_bitrate) < Number(config.bitrate))
    return "Pattern Max bitrate must be at least Target bitrate";
  if (Number(config.klv_pid) === 257) return "Pattern KLV PID must differ from video PID 257";
  if (Number(config.klv_enabled) && Number(config.klv_diagnostics) && Number(config.klv_revision) < 13)
    return "Pattern diagnostics require MISB ST 0601 revision 13 or newer";
  if (Number(config.klv_enabled) && !Number(config.klv_every_frame)) {
    if (!/^\d+$/.test(String(config.klv_rate)) || Number(config.klv_rate) < 1 || Number(config.klv_rate) > 60)
      return "Pattern KLV Rate must be an integer from 1 to 60 Hz";
    if (Number(config.klv_rate) * Number(config.fps_den) > Number(config.fps_num))
      return "Pattern KLV Rate must not exceed video FPS";
  }
  try { if (parseSyntheticKlvTemplateDocument(config.klv_template).revision !== Number(config.klv_revision)) return "Pattern KLV revision must match template tag 65"; }
  catch (error) { return `Pattern KLV template: ${error.message}`; }
  return "";
}
